// tutor-panel — rechte Spalte des Academy-Tabs: EVE-Mentor-Chat.
//
// Verkabelt `useAcademyTutor` mit dem Mockup-Layout (mockups/12-academy):
// Header „EVE // Mentor" + Quiz-Button, scrollbarer Verlauf (User rechts/slate,
// EVE links/amber via MarkdownText), Eingabe-Form unten.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { MarkdownText } from '../../lib/markdown';
import { useAcademyTutor } from '../../hooks/use-academy-tutor';

interface TutorPanelProps {
  moduleTitle: string;
  moduleContext: string;
  onQuizResult?: (passed: boolean, score: number) => void;
}

export function TutorPanel({ moduleTitle, moduleContext, onQuizResult }: TutorPanelProps) {
  const { messages, loading, ask, startQuiz } = useAcademyTutor({
    moduleTitle,
    moduleContext,
    onQuizResult,
  });
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  // Nur nachscrollen, wenn der User unten steht — sonst reisst es ihn beim
  // Lesen nach unten, waehrend EVE noch schreibt.
  const isAtBottomRef = useRef(true);
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // Bei jedem neuen Chunk/Message ans Ende scrollen — aber nur, wenn unten.
  useEffect(() => {
    if (!isAtBottomRef.current) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setInput('');
    void ask(trimmed);
  };

  return (
    <div className="flex h-full flex-col border-l border-slate-800/60 bg-[#070709]/60">
      {/* Header */}
      <div className="border-b border-slate-800/60 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="grid size-9 place-items-center rounded-xl border border-amber-500/30 bg-amber-500/10 text-base shadow-[0_0_22px_-6px_rgba(251,191,36,0.5)]">
              ✦
            </span>
            <div>
              <div className="text-[14px] font-bold text-amber-300">EVE // Mentor</div>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <span className="size-1.5 rounded-full bg-emerald-400" />
                liest mit dir
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void startQuiz()}
            disabled={loading}
            className="rounded-lg border border-amber-500/40 px-3 py-1.5 text-[12px] font-semibold text-amber-300 transition hover:bg-amber-500/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Quiz starten
          </button>
        </div>
      </div>

      {/* Eingabe — direkt unter dem Header, nicht am unteren Rand */}
      <form onSubmit={handleSubmit} className="border-b border-slate-800/60 p-3">
        <div className="flex items-end gap-2 rounded-2xl border border-slate-700/50 bg-white/[0.02] p-2 pl-3.5">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Frage zum Modul …"
            disabled={loading}
            className="flex-1 bg-transparent py-1.5 text-[13.5px] text-slate-200 placeholder:text-slate-500 focus:outline-none disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={loading || input.trim().length === 0}
            title="Senden"
            className="grid size-9 shrink-0 place-items-center rounded-xl bg-amber-500 text-neutral-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ↑
          </button>
        </div>
        <p className="mt-2 px-1 text-center text-[10.5px] text-slate-600">
          EVE kennt den Inhalt dieses Moduls und antwortet im Kontext.
        </p>
      </form>

      {/* Chat-Verlauf */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="scroll-thin flex-1 space-y-4 overflow-y-auto p-4"
      >
        <p className="text-center text-[11px] text-slate-600">Kontext: {moduleTitle}</p>

        {messages.length === 0 && (
          <p className="px-4 pt-6 text-center text-[12.5px] leading-relaxed text-slate-500">
            Frag EVE alles zu diesem Modul — oder starte ein kurzes Quiz, um zu prüfen, ob du es
            verstanden hast.
          </p>
        )}

        {messages.map((m, i) =>
          m.role === 'user' ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[80%] rounded-2xl rounded-tr-sm border border-slate-700/50 bg-slate-800/50 px-4 py-2.5 text-[13.5px] leading-relaxed text-slate-200">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={i} className="flex justify-start">
              <div className="max-w-[88%]">
                <div className="rounded-2xl rounded-tl-sm border border-amber-500/25 bg-amber-500/[0.08] px-4 py-3 text-[13.5px] leading-relaxed text-slate-200">
                  {m.content ? (
                    <MarkdownText text={m.content} />
                  ) : (
                    <span className="inline-flex gap-1 text-amber-300/60">
                      <span className="size-1.5 animate-pulse rounded-full bg-current" />
                      <span className="size-1.5 animate-pulse rounded-full bg-current [animation-delay:150ms]" />
                      <span className="size-1.5 animate-pulse rounded-full bg-current [animation-delay:300ms]" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
}
